import { useState, useEffect } from 'react';
import { Check, ChevronsUpDown, X, Package, Plus, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { useZones, useLocations } from '../../hooks/useWmsLocations';
import { useCatalogSkuSearch, useSku } from '../../hooks/useWmsSkus';
import { useCheckExistingInventory, useAdjustInventory } from '../../hooks/useWmsInventory';
import { useDebounce } from '../../hooks/useDebounce';
import type { CreateManualInventoryInput, SkuSearchResult, SkuPhoto } from '../../types';

function getPhotoThumbnailUrl(photo: SkuPhoto, orgId: string, skuId: string): string | null {
  if (photo.mongoFileId) {
    return `/api/v4/legacy/v2/files/${orgId}/${photo.mongoFileId}-100x100.jpg`;
  }
  if (photo.id && skuId) {
    return `/api/v4/wms/skus/${skuId}/photos/${photo.id}/download`;
  }
  return null;
}

const unitOfMeasureOptions = [
  { value: 'EA', label: 'Each (EA)' },
  { value: 'BOX', label: 'Box' },
  { value: 'ROLL', label: 'Roll' },
  { value: 'PCS', label: 'Pieces' },
  { value: 'KG', label: 'Kilograms' },
  { value: 'M', label: 'Meters' },
  { value: 'L', label: 'Liters' },
];

interface AddInventoryItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateManualInventoryInput) => Promise<void>;
  isLoading?: boolean;
}

export function AddInventoryItemModal({
  open,
  onOpenChange,
  onSubmit,
  isLoading,
}: AddInventoryItemModalProps) {
  const [skuPickerOpen, setSkuPickerOpen] = useState(false);
  const [skuSearch, setSkuSearch] = useState('');
  const [selectedSku, setSelectedSku] = useState<SkuSearchResult | null>(null);
  const [name, setName] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [quantity, setQuantity] = useState<number>(1);
  const [skuCode, setSkuCode] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [lotNumber, setLotNumber] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [unitOfMeasure, setUnitOfMeasure] = useState('EA');
  const [notes, setNotes] = useState('');

  const debouncedSkuSearch = useDebounce(skuSearch, 300);
  const { data: skuResults } = useCatalogSkuSearch(debouncedSkuSearch, 20, open);
  const { data: fullSku } = useSku(selectedSku?.id);

  // Check for existing inventory at selected location
  const mongoSkuId = selectedSku && fullSku?.mongoId ? fullSku.mongoId : undefined;
  const { data: existingItem } = useCheckExistingInventory(mongoSkuId, locationId || undefined);
  const adjustMutation = useAdjustInventory();

  const zonesQuery = useZones();
  const locationsQuery = useLocations({ zone_id: zoneId || undefined, enabled: !!zoneId });

  const zones = zonesQuery.data?.zones || [];
  const locations = locationsQuery.data?.locations || [];

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setSkuPickerOpen(false);
      setSkuSearch('');
      setSelectedSku(null);
      setName('');
      setZoneId('');
      setLocationId('');
      setQuantity(1);
      setSkuCode('');
      setSupplierName('');
      setLotNumber('');
      setExpirationDate('');
      setUnitCost('');
      setUnitOfMeasure('EA');
      setNotes('');
    }
  }, [open]);

  // Prefill fields when full SKU data loads
  useEffect(() => {
    if (fullSku && selectedSku) {
      setSkuCode(fullSku.sku || '');
      if (fullSku.cost) setUnitCost(fullSku.cost);
      // Prefer first supplier's org name, fall back to manufacturer
      const supplier = fullSku.suppliers?.[0]?.organisationName || fullSku.manufacturerName;
      if (supplier) setSupplierName(supplier);
    }
  }, [fullSku, selectedSku]);

  // Reset location when zone changes
  useEffect(() => {
    setLocationId('');
  }, [zoneId]);

  const handleSelectSku = (sku: SkuSearchResult) => {
    setSelectedSku(sku);
    setName(sku.nameFr || sku.nameEn);
    setSkuCode(sku.sku);
    if (sku.manufacturerName) setSupplierName(sku.manufacturerName);
    setSkuPickerOpen(false);
    setSkuSearch('');
  };

  const handleCreateNew = () => {
    setSelectedSku(null);
    setName(skuSearch.trim());
    setSkuCode('');
    setSupplierName('');
    setUnitCost('');
    setSkuPickerOpen(false);
    setSkuSearch('');
  };

  const handleClearSku = () => {
    setSelectedSku(null);
    setName('');
    setSkuCode('');
    setSupplierName('');
    setUnitCost('');
  };

  const canSubmit = name.trim() && locationId && quantity > 0 && !isLoading && !adjustMutation.isPending;

  // Track whether the user used "create on the fly" (no selectedSku but has a name)
  const isNewProduct = !selectedSku && !!name.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (existingItem) {
      // Adjust existing stock: add quantity to current
      await adjustMutation.mutateAsync({
        inventory_item_id: existingItem.id,
        new_quantity: existingItem.quantityOnHand + quantity,
        reason_code: 'manual_add',
        notes: notes.trim() || `Manual stock addition (+${quantity})`,
      });
      onOpenChange(false);
    } else {
      await onSubmit({
        name: name.trim(),
        location_id: locationId,
        quantity,
        mongo_sku_id: selectedSku && fullSku?.mongoId ? fullSku.mongoId : undefined,
        create_new_sku: isNewProduct || undefined,
        sku_code: skuCode.trim() || undefined,
        supplier_name: supplierName.trim() || undefined,
        lot_number: lotNumber.trim() || undefined,
        expiration_date: expirationDate || undefined,
        unit_cost: unitCost ? parseFloat(unitCost) : undefined,
        unit_of_measure: unitOfMeasure || undefined,
        notes: notes.trim() || undefined,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md overflow-hidden">
        <DialogHeader>
          <DialogTitle>Add Inventory Item</DialogTitle>
          <DialogDescription>
            Manually add an item to inventory. SKU is auto-generated if not provided.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="min-w-0 overflow-hidden">
          <div className="space-y-4 py-4">
            {/* Required Fields */}
            <div className="space-y-2">
              <Label>Product *</Label>
              <Popover open={skuPickerOpen} onOpenChange={setSkuPickerOpen} modal>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={skuPickerOpen}
                    className="w-full justify-between font-normal overflow-hidden"
                  >
                    {selectedSku ? (
                      <span className="truncate min-w-0">{selectedSku.nameFr || selectedSku.nameEn}</span>
                    ) : name ? (
                      <span className="truncate min-w-0">{name}</span>
                    ) : (
                      <span className="text-muted-foreground">Rechercher un produit...</span>
                    )}
                    {(selectedSku || name) ? (
                      <X
                        className="ml-2 h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                        onClick={(e) => { e.stopPropagation(); handleClearSku(); }}
                      />
                    ) : (
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Rechercher par nom français..."
                      value={skuSearch}
                      onValueChange={setSkuSearch}
                    />
                    <CommandList>
                      <CommandEmpty>Aucun produit trouvé</CommandEmpty>
                      <CommandGroup>
                        {skuResults?.map((sku) => (
                          <CommandItem
                            key={sku.id}
                            value={sku.id}
                            onSelect={() => handleSelectSku(sku)}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedSku?.id === sku.id ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            <div className="min-w-0">
                              <div className="truncate">{sku.nameFr || sku.nameEn}</div>
                              <div className="text-xs text-muted-foreground truncate">
                                {sku.sku}
                                {sku.manufacturerName ? ` · ${sku.manufacturerName}` : ''}
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                      {skuSearch.trim() && (
                        <CommandGroup forceMount>
                          <CommandItem
                            value="__create_new__"
                            onSelect={handleCreateNew}
                            className="border-t"
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            <span>
                              Créer <span className="font-medium">"{skuSearch.trim()}"</span> comme nouveau produit
                            </span>
                          </CommandItem>
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedSku && fullSku && (
                <div className="flex items-start gap-3 rounded-md border bg-muted/30 p-2.5 overflow-hidden">
                  {fullSku.photos?.length ? (
                    <img
                      src={getPhotoThumbnailUrl(fullSku.photos[0], fullSku.organisationId, fullSku.id) || undefined}
                      alt={fullSku.nameFr}
                      className="h-14 w-14 shrink-0 rounded-md border object-cover bg-white"
                    />
                  ) : (
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border bg-white">
                      <Package className="h-6 w-6 text-muted-foreground/50" />
                    </div>
                  )}
                  <div className="min-w-0 text-xs leading-relaxed">
                    <p className="font-medium truncate">{fullSku.nameFr || fullSku.nameEn}</p>
                    <p className="font-mono text-muted-foreground">{fullSku.sku}</p>
                    {fullSku.manufacturerName && (
                      <p className="text-muted-foreground truncate">
                        {fullSku.manufacturerName}
                        {fullSku.model ? ` · ${fullSku.model}` : ''}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="zone">Zone *</Label>
                <Select value={zoneId} onValueChange={setZoneId}>
                  <SelectTrigger id="zone">
                    <SelectValue placeholder="Select zone" />
                  </SelectTrigger>
                  <SelectContent>
                    {zones.map((z) => (
                      <SelectItem key={z.id} value={z.id}>
                        {z.code} - {z.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Location *</Label>
                <Select
                  value={locationId}
                  onValueChange={setLocationId}
                  disabled={!zoneId}
                >
                  <SelectTrigger id="location">
                    <SelectValue placeholder={zoneId ? 'Select bin' : 'Select zone first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.bin_code}{loc.name ? ` - ${loc.name}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {existingItem && (
              <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm">
                <Info className="h-4 w-4 mt-0.5 shrink-0 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">
                    This product already has {existingItem.quantityOnHand} {existingItem.unitOfMeasure || 'units'} at this location.
                  </p>
                  <p className="text-blue-700 mt-0.5">
                    Your quantity will be added to the existing stock.
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity *</Label>
              <Input
                id="quantity"
                type="number"
                value={quantity === 0 ? '' : quantity}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setQuantity(0);
                    return;
                  }
                  const val = parseInt(value, 10);
                  if (!isNaN(val) && val >= 0 && val <= 999999) {
                    setQuantity(val);
                  }
                }}
                min={1}
                max={999999}
                required
              />
            </div>

            {!existingItem && (
              <>
                <Separator />

                {/* Optional Fields */}
                <div className="space-y-2">
                  <Label htmlFor="sku_code">SKU {selectedSku ? '' : '(optional)'}</Label>
                  <Input
                    id="sku_code"
                    placeholder="Auto-generated if empty"
                    value={skuCode}
                    onChange={(e) => setSkuCode(e.target.value.slice(0, 50))}
                    readOnly={!!selectedSku}
                    className={selectedSku ? 'bg-muted' : ''}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="supplier_name">Supplier (optional)</Label>
                  <Input
                    id="supplier_name"
                    placeholder="Supplier name"
                    value={supplierName}
                    onChange={(e) => setSupplierName(e.target.value.slice(0, 255))}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="lot_number">Lot # (optional)</Label>
                    <Input
                      id="lot_number"
                      placeholder="Lot number"
                      value={lotNumber}
                      onChange={(e) => setLotNumber(e.target.value.slice(0, 50))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="expiration_date">Expiration (optional)</Label>
                    <Input
                      id="expiration_date"
                      type="date"
                      value={expirationDate}
                      onChange={(e) => setExpirationDate(e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="unit_cost">Unit Cost (optional)</Label>
                    <Input
                      id="unit_cost"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={unitCost}
                      onChange={(e) => setUnitCost(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="unit_of_measure">UOM (optional)</Label>
                    <Select value={unitOfMeasure} onValueChange={setUnitOfMeasure}>
                      <SelectTrigger id="unit_of_measure">
                        <SelectValue placeholder="Unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {unitOfMeasureOptions.map((u) => (
                          <SelectItem key={u.value} value={u.value}>
                            {u.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Additional details..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {(isLoading || adjustMutation.isPending) ? 'Adding...' : existingItem ? `Add to Stock (+${quantity})` : 'Add Item'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
